import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useGetResolverJobDisplayName } from "@/hooks/useJobDisplayName";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { FONT_SIZE, INK, OUTREACH_POPUP } from "@/theme/tokens";
import MetaChip from "@/ui/Badges/MetaChip";
import { FactTile } from "@/ui/molecules/FactTile";
import { OutreachApproval, type RequestOutreach, ShiftType } from "@/utils/models";
import { DATE_FORMAT, timeRangeOf } from "./useOutreachCopy";

type Props = { outreach: RequestOutreach };

/** "Aide" — or, in a department with jobs, the job (and sub-role) the request is for, as its card shows it. */
const useNeededWhat = (shift: RequestOutreach["shift"]): string => {
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const resolveJobDisplayName = useGetResolverJobDisplayName();
	const jobName = jobManagementEnabled && shift.jobId !== null ? resolveJobDisplayName(shift.jobId) : null;
	// DEFAULT: a job that doesn't resolve shows the request's roles, as the request card (RequestItem) does, so the
	// two never disagree about what's needed.
	return (jobName ? [jobName, ...(shift.subRole === null ? [] : [shift.subRole])] : shift.roles).join(", ");
};

/** Who's awaiting approval, who's on the shift, and who said no. */
const useAnswersLine = (outreach: RequestOutreach): string => {
	const { t } = useTranslation();
	const waiting = outreach.signUps.filter((signUp) => signUp.approval === OutreachApproval.Waiting).length;
	const { approved, declined } = outreach.counts;
	const parts = [
		...(waiting > 0 ? [t("request.outreach.answers.awaiting_approval", { count: waiting })] : []),
		...(approved > 0 ? [t("request.outreach.answers.approved", { count: approved })] : []),
		...(declined > 0 ? [t("request.outreach.answers.declined", { count: declined })] : []),
	];
	return parts.length > 0 ? parts.join(" · ") : t("request.outreach.answers.none");
};

/** The shift's basics, then how far outreach has got: date, hours, who's needed, and who we've reached. */
export const OutreachShiftFacts: FC<Props> = ({ outreach }) => {
	const { t } = useTranslation();
	const { shift, counts } = outreach;
	const what = useNeededWhat(shift);
	const answers = useAnswersLine(outreach);
	const isDay = shift.shiftType === ShiftType.Day;

	return (
		<Box
			data-testid="outreach-shift"
			sx={{ display: "flex", gap: 1.5, alignItems: "stretch", flexWrap: { xs: "wrap", sm: "nowrap" }, "& > *": { minWidth: { xs: "40%", sm: 0 } } }}
		>
			<FactTile
				icon={<CalendarMonthRoundedIcon fontSize="inherit" />}
				label={t("date")}
				value={shift.start.toFormat(DATE_FORMAT)}
				dataTestId="outreach-fact-date"
			/>
			<FactTile
				icon={<ScheduleRoundedIcon fontSize="inherit" />}
				label={t("time")}
				value={
					<>
						<img src={isDay ? "/sun.svg" : "/moon.svg"} alt={t(isDay ? "day.shift" : "night.shift")} />
						{timeRangeOf(shift.start, shift.end)}
					</>
				}
				dataTestId="outreach-fact-time"
			/>
			<FactTile
				icon={<PersonRoundedIcon fontSize="inherit" />}
				label={t("request.outreach.facts.needed")}
				value={
					<>
						{shift.neededCount}
						<MetaChip size="small" tone="primary" label={what} />
					</>
				}
				dataTestId="outreach-needed"
			/>
			<FactTile
				icon={<ChatBubbleOutlineRoundedIcon fontSize="inherit" />}
				label={t("request.outreach.facts.reached")}
				value={
					<>
						<span data-testid="outreach-heard-count">{counts.asked}</span>
						<Box component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 500, color: INK.SECONDARY }}>
							{t("request.outreach.facts.reached_of", { eligible: counts.eligible })}
						</Box>
					</>
				}
				caption={<span data-testid="outreach-answers">{answers}</span>}
				dataTestId="outreach-fact-reached"
				sx={{ flexGrow: OUTREACH_POPUP.REACHED_TILE_GROW }}
			/>
		</Box>
	);
};
